import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { mergeApi, errMsg } from "../institutionMerge.api";
import { fmtDate } from "../institutionMerge.constants";
import { FullRecordModal } from "./FullRecordModal";
import { afterCol, beforeCol } from "./recordDisplay";

const { Text } = Typography;
const PHASES = {
  target: "cible",
  refs: "références",
  blacklists: "listes noires",
  users: "comptes",
  dependencies: "dépendances (adresses, responsables)",
  archive: "archivage",
  restore: "restauration",
};
const OPS = {
  set_scalar: "champ",
  set_array: "liste",
  update_doc: "document",
  insert_doc: "création",
  delete_doc: "suppression",
};
const OID = /^[a-f0-9]{24}$/i;
/** Render a journal value: a name when we have one, a readable object otherwise, an id tail as last resort. */
const short = (v, labels = {}) => {
  if (v == null) return "—";
  if (Array.isArray(v))
    return v.length ? v.map(x => short(x, labels)).join(", ") : "(vide)";
  if (typeof v === "object")
    return Object.entries(v)
      .map(([k, x]) => `${k}: ${short(x, labels)}`)
      .join(" · ")
      .slice(0, 160);
  const s = String(v);
  if (OID.test(s)) return labels[s] || `…${s.slice(-6)}`;
  if (typeof v === "boolean") return v ? "oui" : "non";
  return s.length > 60 ? s.slice(0, 60) + "…" : s;
};

/**
 * One compact row expanded into per-document lines: before → after, with the value the document
 * holds right now — and, per line, the door to the document's complete card (every field).
 */
function RowItems({ mergeId, seq, model, open, onClose, onOpenJournal }) {
  const [s, setS] = useState({
    items: [],
    total: 0,
    page: 1,
    loading: false,
    field: "",
    labels: {},
    model: undefined,
  });
  const [card, setCard] = useState(null);
  const load = useCallback(
    async page => {
      setS(x => ({ ...x, loading: true }));
      try {
        const d = await mergeApi.journalItems(mergeId, seq, page, 50);
        setS({
          items: d.items,
          total: d.total,
          page,
          loading: false,
          field: d.field,
          labels: d.labels || {},
          model: d.model,
        });
      } catch (e) {
        notification.error({ message: "Erreur", description: errMsg(e) });
        setS(x => ({ ...x, loading: false }));
      }
    },
    [mergeId, seq],
  );
  useEffect(() => {
    if (open) load(1);
  }, [open, load]);
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      title={`Journal #${seq} — ${s.field || ""} (${s.total})`}
    >
      <Table
        size="small"
        rowKey="documentId"
        loading={s.loading}
        dataSource={s.items}
        pagination={{
          current: s.page,
          pageSize: 50,
          total: s.total,
          onChange: load,
          showSizeChanger: false,
        }}
        columns={[
          { title: "Enregistrement", dataIndex: "label" },
          {
            title: "Avant",
            dataIndex: "before",
            ...beforeCol,
            render: v => (
              <Text delete type="secondary">
                {short(v, s.labels)}
              </Text>
            ),
          },
          {
            title: "Après",
            dataIndex: "after",
            ...afterCol,
            render: v => <Text strong>{short(v, s.labels)}</Text>,
          },
          {
            title: "Actuellement",
            dataIndex: "current",
            render: v => short(v, s.labels),
          },
          {
            title: "",
            dataIndex: "ok",
            width: 60,
            render: ok =>
              ok ? <Tag color="green">✔</Tag> : <Tag color="red">✗</Tag>,
          },
          {
            title: "",
            key: "card",
            width: 130,
            render: (_, it) => {
              const m = it.model || model || s.model;
              return m && it.documentId ? (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() =>
                    setCard({
                      model: m,
                      documentId: it.documentId,
                      label: it.label,
                    })
                  }
                >
                  Fiche complète
                </Button>
              ) : null;
            },
          },
        ]}
      />
      {card && (
        <FullRecordModal
          open
          mergeId={mergeId}
          model={card.model}
          documentId={card.documentId}
          label={card.label}
          onClose={() => setCard(null)}
          onOpenJournal={
            onOpenJournal
              ? sq => {
                  setCard(null);
                  onOpenJournal(sq);
                }
              : undefined
          }
        />
      )}
    </Modal>
  );
}

/** For array rows, the operator wants the difference, not two 15-item lists. */
const arrayDiff = (before, after, labels) => {
  const b = (before || []).map(String);
  const a = (after || []).map(String);
  const removed = b.filter(x => !a.includes(x));
  const added = a.filter(x => !b.includes(x));
  return {
    removed: removed.map(x => short(x, labels)),
    added: added.map(x => short(x, labels)),
    unchanged: b.length - removed.length,
  };
};
const DiffCell = ({ row, side, labels }) => {
  const plain =
    side === "from"
      ? row.op === "set_scalar"
        ? row.from
        : row.before
      : row.op === "set_scalar"
      ? row.to
      : row.after;
  if (row.op !== "set_array" || (row.meta && row.meta.nested))
    return short(plain, labels);
  const d = arrayDiff(row.before, row.after, labels);
  if (side === "from")
    return d.removed.length ? (
      <span>
        {d.removed.map(x => (
          <Tag key={x} color="red">
            − {x}
          </Tag>
        ))}
        {d.unchanged > 0 && (
          <Text type="secondary">+ {d.unchanged} inchangé(s)</Text>
        )}
      </span>
    ) : (
      <Text type="secondary">—</Text>
    );
  return d.added.length ? (
    d.added.map(x => (
      <Tag key={x} color="green">
        + {x}
      </Tag>
    ))
  ) : (
    <Text type="secondary">(retiré uniquement)</Text>
  );
};

/** The before/after browser — the journal as the operator reads it. */
export function MergeJournal({ mergeId, initialFilters }) {
  const [s, setS] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    loading: false,
    labels: {},
  });
  const [filters, setFilters] = useState(() => {
    const { seq, openedAt, ...rest } = initialFilters || {};
    return { model: undefined, phase: undefined, field: undefined, ...rest };
  });
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!initialFilters) return;
    if (initialFilters.seq != null) {
      // a row number from a « fiche complète »: open that row's documents directly
      setOpen({ seq: initialFilters.seq });
      return;
    }
    // a new filter from outside: drop the rows of the previous query so nothing stale shows while reloading
    setS(x => ({ ...x, items: [], total: 0, loading: true }));
    const { seq, openedAt, ...rest } = initialFilters;
    setFilters(f => ({ ...f, ...rest }));
  }, [initialFilters]);
  const load = useCallback(
    async (page = 1, limit = 50) => {
      setS(x => ({ ...x, loading: true }));
      try {
        const d = await mergeApi.journal(mergeId, { page, limit, ...filters });
        setS({ ...d, loading: false });
      } catch (e) {
        notification.error({ message: "Erreur", description: errMsg(e) });
        setS(x => ({ ...x, loading: false }));
      }
    },
    [mergeId, filters],
  );
  useEffect(() => {
    load(1, s.limit); /* eslint-disable-next-line */
  }, [load]);

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space>
        <Select
          allowClear
          placeholder="Collection"
          style={{ width: 160 }}
          value={filters.model}
          onChange={model => setFilters(f => ({ ...f, model }))}
          options={[
            "Prestation",
            "Bill",
            "Address",
            "User",
            "Responsable",
            "Batch",
            "Principal",
            "Institution",
          ].map(m => ({ value: m, label: m }))}
        />
        <Select
          allowClear
          placeholder="Phase"
          style={{ width: 160 }}
          value={filters.phase}
          onChange={phase => setFilters(f => ({ ...f, phase }))}
          options={Object.entries(PHASES).map(([k, v]) => ({
            value: k,
            label: v,
          }))}
        />
        {filters.field && (
          <Tag
            closable
            onClose={() => setFilters(f => ({ ...f, field: undefined }))}
          >
            champ : {filters.field}
          </Tag>
        )}
        <Text type="secondary">{s.total} ligne(s) de journal</Text>
      </Space>
      <Table
        size="small"
        rowKey="_id"
        loading={s.loading}
        dataSource={s.items}
        onRow={r => ({
          onClick: () => setOpen({ seq: r.seq, model: r.model }),
          style: { cursor: "pointer" },
        })}
        pagination={{
          current: s.page,
          pageSize: s.limit,
          total: s.total,
          onChange: load,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100, 200],
        }}
        columns={[
          { title: "#", dataIndex: "seq", width: 70 },
          {
            title: "Phase",
            dataIndex: "phase",
            width: 120,
            render: p => (
              <Tag color={p === "restore" ? "purple" : "blue"}>
                {PHASES[p] || p}
              </Tag>
            ),
          },
          {
            title: "Opération",
            dataIndex: "op",
            width: 110,
            render: o => OPS[o] || o,
          },
          { title: "Collection", dataIndex: "model", width: 120 },
          {
            title: "Champ",
            dataIndex: "field",
            render: (f, r) =>
              f ||
              (r.op === "insert_doc"
                ? "(nouveau document)"
                : r.op === "update_doc"
                ? Object.keys(r.after || {}).join(", ")
                : "—"),
          },
          {
            title: "De",
            dataIndex: "from",
            ...beforeCol,
            render: (v, r) => (
              <DiffCell row={r} side="from" labels={s.labels} />
            ),
          },
          {
            title: "Vers",
            dataIndex: "to",
            ...afterCol,
            render: (v, r) => <DiffCell row={r} side="to" labels={s.labels} />,
          },
          {
            title: "Documents",
            dataIndex: "count",
            align: "right",
            width: 100,
          },
          {
            title: "Appliqué",
            dataIndex: "appliedAt",
            width: 160,
            render: (d, r) =>
              r.undoneAt ? (
                <Tag color="purple">annulé</Tag>
              ) : d ? (
                fmtDate(d)
              ) : (
                <Tag color="orange">en attente</Tag>
              ),
          },
        ]}
      />
      {open != null && (
        <RowItems
          key={open.seq}
          mergeId={mergeId}
          seq={open.seq}
          model={
            open.model || (s.items.find(r => r.seq === open.seq) || {}).model
          }
          open
          onClose={() => setOpen(null)}
          onOpenJournal={seq => setOpen({ seq })}
        />
      )}
    </Space>
  );
}
